import {
  Badge,
  BeekLogo,
  DeviceSizes,
  Icon,
  Link,
  Typography,
} from 'beek-design-system';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';

import Const from 'src/config/Const';

type FooterProps = {
  changeLanguage: (value: string) => void;
  getLanguage: () => string | undefined;
  paddingBottom?: boolean;
  paddingTop?: boolean;
};

type ListContainerProps = {
  openDiscovery?: boolean;
  openCompany?: boolean;
  openSupport?: boolean;
};

type ListInsideProps = {
  paddingBottom?: boolean;
  paddingTop?: boolean;
};

type DividerStyledProps = {
  marginBottom?: string;
};

const defaultProps = {
  paddingBottom: true,
  paddingTop: false,
};

const FlexColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
`;

const BadgesMobile = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding: 0;
`;

const FlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FlexContainer = styled.div`
  display: flex;
`;

const ListContainer = styled.li<ListContainerProps>`
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  padding-right: 0.5rem;
  #discovery {
    display: ${(props): string => (props.openDiscovery ? 'block' : 'none')};
  }
  #company {
    display: ${(props): string => (props.openCompany ? 'block' : 'none')};
  }
  #support {
    display: ${(props): string => (props.openSupport ? 'block' : 'none')};
  }
`;

const ListInside = styled.li<ListInsideProps>`
  padding-left: 0.5rem;
  padding-top: ${(props): string => (props.paddingTop ? '0.5rem' : '0')};
  padding-bottom: ${(props): string => (props.paddingBottom ? '0.5rem' : '0')};
`;

const GridContainer = styled.div`
  display: grid;
  align-items: center;
  justify-items: center;
  grid-template-columns: 7.1rem 0.625rem 8.3rem 0.625rem;
  @media ${DeviceSizes.mobileM} {
    grid-template-columns: 7.1rem 0.625rem 8.3rem 0.625rem 5.625rem;
  }
`;

const DividerStyled = styled.hr<DividerStyledProps>`
  border-top: 0.063rem solid var(--gray-15);
  margin-bottom: ${(props): string =>
    props.marginBottom ? props.marginBottom : '0'};
`;

const StyledSelect = styled.select`
  border: none;
  color: var(--gray-70);
  font-family: var(--primary-font);
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5rem;
  background-color: transparent;
`;

const FooterMobile = (props: FooterProps): JSX.Element => {
  const t = useTranslations('articles');
  const { getLanguage, changeLanguage } = { ...props };
  const [openDiscovery, setOpenDiscovery] = useState(false);
  const [openCompany, setOpenCompany] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);

  const handleIconPlus = (category: string): void => {
    if (category === '#discovery') {
      setOpenDiscovery(!openDiscovery);
    } else if (category === '#company') {
      setOpenCompany(!openCompany);
    } else {
      setOpenSupport(!openSupport);
    }
  };

  return (
    <>
      <BeekLogo variant="normal" />
      <FlexColumnContainer>
        <ul>
          <DividerStyled />
          <ListContainer openDiscovery={openDiscovery}>
            <FlexSpaceBetween
              onClick={(): void => handleIconPlus('#discovery')}
            >
              <Typography type="body2" colorVariant="gray70">
                {`${t('footer.discovery')}`}
              </Typography>
              {openDiscovery ? (
                <Icon name="close" colorVariant="gray70" marginTop="0.375rem" />
              ) : (
                <Icon name="plus" colorVariant="gray70" marginTop="0.375rem" />
              )}
            </FlexSpaceBetween>
            <ul id="discovery">
              <ListInside paddingBottom paddingTop>
                <Link
                  href="/html_sitemap/audiolibros_a.html"
                  textDecoration="none"
                  typographyType="caption"
                  colorVariant="gray70"
                >
                  {`${t('footer.audiobooks')}`}
                </Link>
              </ListInside>
              <ListInside paddingBottom={false}>
                <Link
                  href="/listas"
                  textDecoration="none"
                  typographyType="caption"
                  colorVariant="gray70"
                >
                  {`${t('footer.articles')}`}
                </Link>
              </ListInside>
            </ul>
          </ListContainer>
          <DividerStyled />
          <ListContainer openCompany={openCompany}>
            <FlexSpaceBetween onClick={(): void => handleIconPlus('#company')}>
              <Typography type="body2" colorVariant="gray70">
                {`${t('footer.company')}`}
              </Typography>
              {openCompany ? (
                <Icon name="close" colorVariant="gray70" marginTop="0.375rem" />
              ) : (
                <Icon name="plus" colorVariant="gray70" marginTop="0.375rem" />
              )}
            </FlexSpaceBetween>
            <ul id="company">
              <ListInside paddingBottom paddingTop>
                <Link
                  href="https://airtable.com/shrTI11sflvyIlfua"
                  textDecoration="none"
                  typographyType="caption"
                  colorVariant="gray70"
                  external
                >
                  {`${t('footer.becomeCreator')}`}
                </Link>
              </ListInside>
              <ListInside paddingBottom>
                <Link
                  href="https://beek.breezy.hr/"
                  textDecoration="none"
                  typographyType="caption"
                  colorVariant="gray70"
                >
                  {`${t('footer.work')}`}
                </Link>
              </ListInside>
            </ul>
          </ListContainer>
          <DividerStyled marginBottom="0.5rem" />
          <ListContainer openSupport={openSupport}>
            <FlexSpaceBetween onClick={(): void => handleIconPlus('#support')}>
              <Typography type="body2" colorVariant="gray70">
                {`${t('footer.support')}`}
              </Typography>
              {openSupport ? (
                <Icon name="close" colorVariant="gray70" marginTop="0.375rem" />
              ) : (
                <Icon name="plus" colorVariant="gray70" marginTop="0.375rem" />
              )}
            </FlexSpaceBetween>
            <ul id="support">
              <ListInside paddingBottom paddingTop>
                <Link
                  href="https://www.notion.so/Preguntas-frecuentes-43a0f97ad89d46acb055ff37b961c8ac"
                  textDecoration="none"
                  typographyType="caption"
                  colorVariant="gray70"
                >
                  {`${t('footer.frequentQuestions')}`}
                </Link>
              </ListInside>
              <ListInside paddingBottom>
                <Link
                  href={Const.supportUrl}
                  textDecoration="none"
                  typographyType="caption"
                  colorVariant="gray70"
                >
                  {`${t('footer.help')}`}
                </Link>
              </ListInside>
            </ul>
          </ListContainer>
          <DividerStyled />
        </ul>
      </FlexColumnContainer>
      <BadgesMobile>
        <Badge variant="applestore" marginRight="0.25rem" />
        <Badge variant="playstore" />
      </BadgesMobile>
      <FlexSpaceBetween style={{ marginTop: '1.5rem', marginBottom: '2rem' }}>
        <FlexContainer>
          <StyledSelect
            onChange={(e): string | void => changeLanguage(e.target.value)}
            value={getLanguage()}
            data-testid="select-language"
          >
            <option value="es">{`${t('footer.languageEs')}`}</option>
            <option value="en">{`${t('footer.languageEn')}`}</option>
          </StyledSelect>
        </FlexContainer>
        <FlexContainer>
          <Icon
            name="facebook"
            colorVariant="gray70"
            url="https://www.facebook.com/beekio"
            external
            marginRight="1.625rem"
          />
          <Icon
            name="twitter"
            colorVariant="gray70"
            url="https://twitter.com/beek_io"
            external
            marginRight="1.625rem"
            marginTop="0.125rem"
          />
          <Icon
            name="instagram"
            colorVariant="gray70"
            url="https://www.instagram.com/beek_io/"
            external
            marginRight="1.625rem"
          />
          <Icon
            name="youtube"
            colorVariant="gray70"
            url="https://www.youtube.com/c/BeekIo"
            external
            marginTop="0.25rem"
          />
        </FlexContainer>
      </FlexSpaceBetween>
      <Typography type="caption" colorVariant="gray50">
        {`${t('footer.copyright', {
          currentYear: new Date().getFullYear(),
        })}`}
      </Typography>
      <GridContainer style={{ marginTop: '0.75rem' }}>
        <Link
          href="/aviso_privacidad.pdf"
          external
          textDecoration="none"
          typographyType="caption"
          colorVariant="gray70"
          locale={false}
        >
          {`${t('footer.privacy')}`}
        </Link>
        <Typography display="inline" type="caption" colorVariant="gray70">
          |
        </Typography>
        <Link
          href="/terms.pdf"
          external
          textDecoration="none"
          typographyType="caption"
          colorVariant="gray70"
          locale={false}
        >
          {`${t('footer.terms')}`}
        </Link>
        <Typography display="inline" type="caption" colorVariant="gray70">
          |
        </Typography>
        <Link
          href="/sitemap.xml"
          external
          textDecoration="none"
          typographyType="caption"
          colorVariant="gray70"
          locale={false}
        >
          {`${t('footer.map')}`}
        </Link>
      </GridContainer>
    </>
  );
};

FooterMobile.defaultProps = defaultProps;

export default FooterMobile;
